import React, { useState, useMemo } from 'react';
import Logo from '../shared/brand/Logo';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ModalShell from '../shared/ModalShell';
import {
  PiTableLight,
  PiCheckCircleLight,
  PiColumnsLight,
  PiTextTLight,
  PiCalendarLight,
  PiHashLight,
  PiToggleLeftLight,
  PiCurrencyDollarLight,
  PiUserLight,
  PiBuildingsLight,
  PiGlobeLight,
} from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import { TableTemplate, TemplateColumn, ColumnDataType } from '../../types/batchTables';
import PersonCell from '../shared/access/PersonCell';

interface TemplatePickerModalProps {
  templates: TableTemplate[];
  selectedTemplateId: string;
  onSelect: (templateId: string) => void;
  onClose: () => void;
}

type FilterTab = 'all' | 'mine' | 'firm' | 'pons';

const dataTypeIcon: Record<ColumnDataType | string, React.ReactNode> = {
  text: <PiTextTLight className="w-3.5 h-3.5" />,
  date: <PiCalendarLight className="w-3.5 h-3.5" />,
  number: <PiHashLight className="w-3.5 h-3.5" />,
  boolean: <PiToggleLeftLight className="w-3.5 h-3.5" />,
  currency: <PiCurrencyDollarLight className="w-3.5 h-3.5" />,
};

const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({
  templates,
  selectedTemplateId,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [pendingId, setPendingId] = useState<string>(selectedTemplateId);

  // Counts per filter tab
  const counts = useMemo(() => {
    const all = templates.length;
    const pons = templates.filter((t) => t.is_global).length;
    const firm = templates.filter((t) => !t.is_global).length;
    // "mine" = created by current user - we don't have current user id here,
    // so approximate: non-global templates where owner exists
    const mine = templates.filter((t) => !t.is_global && t.owner).length;
    return { all, pons, firm, mine };
  }, [templates]);

  // Apply filter + search
  const filtered = useMemo(() => {
    let list = templates;

    // Filter by tab
    if (activeFilter === 'pons') list = list.filter((t) => t.is_global);
    else if (activeFilter === 'firm') list = list.filter((t) => !t.is_global);
    else if (activeFilter === 'mine') list = list.filter((t) => !t.is_global && t.owner);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (tmpl) =>
          tmpl.name.toLowerCase().includes(q) ||
          tmpl.description?.toLowerCase().includes(q) ||
          tmpl.category?.toLowerCase().includes(q) ||
          tmpl.columns?.some((c) => c.name.toLowerCase().includes(q))
      );
    }

    return list;
  }, [templates, activeFilter, search]);

  const handleCardClick = (tmplId: string) => {
    setPendingId(tmplId === pendingId ? '' : tmplId);
  };

  const handleConfirm = () => {
    onSelect(pendingId);
    onClose();
  };

  const filterTabs: { key: FilterTab; label: string; icon: React.ReactNode; count: number }[] = [
    { key: 'all', label: t('All'), icon: <PiTableLight className="w-4 h-4" />, count: counts.all },
    { key: 'mine', label: t('My templates'), icon: <PiUserLight className="w-4 h-4" />, count: counts.mine },
    { key: 'firm', label: t('Firm'), icon: <PiBuildingsLight className="w-4 h-4" />, count: counts.firm },
    { key: 'pons', label: t('PONS'), icon: <Logo variant="mark" whiteLabelAware={false} className="w-4 h-4" />, count: counts.pons },
  ];

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      title={t('Select template')}
      description={t('Choose a template to pre-fill columns for your table')}
      icon={<PiTableLight />}
      size="full"
      height="tall"
      bodyClassName="flex p-0 overflow-hidden"
      zIndexClassName="z-[300000000]"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {filtered.length} {t('templates available')}
              {pendingId && (
                <span className="ms-2 text-primary-600 dark:text-primary-400 font-medium">
                  - 1 {t('selected')}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {pendingId && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={() => {
                    onSelect('');
                    onClose();
                  }}
                >
                  {t('Clear selection')}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleConfirm}
                disabled={!pendingId}
              >
                {t('Confirm')}
              </Button>
            </div>
        </div>
      }
    >
            {/* Left sidebar - filters */}
            <div className="w-56 flex-shrink-0 border-e border-hairline-soft bg-surface-sunken/40 p-4 flex flex-col gap-1">
              <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mb-2 px-2">
                {t('Filter by')}
              </p>
              {filterTabs.map((tab) => (
                <button
                  type="button"
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full text-start ${
                    activeFilter === tab.key
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {tab.icon}
                  <span className="flex-1">{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === tab.key
                      ? 'bg-primary-100 dark:bg-primary-800/50 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}

              {/* Category filters if there are categories */}
              {(() => {
                const categories = [...new Set(templates.map((t) => t.category).filter(Boolean))] as string[];
                if (categories.length === 0) return null;
                return (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-3" />
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mb-2 px-2">
                      {t('Categories')}
                    </p>
                    <div className='max-h-[calc(100vh-500px)] overflow-y-auto custom-scrollbar-main'>
                    {categories.map((cat) => (
                      <button
                        type="button"
                        key={cat}
                        onClick={() => setSearch(cat)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors w-full text-start capitalize"
                      >
                        {cat.replace(/_/g, ' ')}
                      </button>
                    ))}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Right content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search bar */}
              <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t('Search templates...')}
                />
              </div>

              {/* Template grid */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {filtered.length === 0 ? (
                  <div className="text-center py-16">
                    <PiTableLight className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {search ? t('No templates match your search') : t('No templates available')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {filtered.map((tmpl) => {
                      const isSelected = String(tmpl.id) === pendingId;
                      const isPons = tmpl.is_global;
                      return (
                        <motion.div
                          key={tmpl.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => handleCardClick(String(tmpl.id))}
                          className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all hover:shadow-md ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10 shadow-sm ring-1 ring-primary-200 dark:ring-primary-800'
                              : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 bg-white dark:bg-gray-800/50'
                          }`}
                        >
                          {/* Selected badge */}
                          {isSelected && (
                            <div className="absolute top-3 end-3">
                              <PiCheckCircleLight className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                          )}

                          {/* Template header */}
                          <div className="flex items-start gap-3 mb-3">
                            <div className={`p-2 rounded-lg flex-shrink-0 ${isSelected ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-gray-100 dark:bg-gray-700'}`}>
                              <PiTableLight className={`w-4 h-4 ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate pe-6">
                                {tmpl.name}
                              </h3>
                              {tmpl.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {tmpl.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5">
                                <Badge color={isSelected ? 'blue' : 'gray'} className="text-[10px]">
                                  <PiColumnsLight className="w-3 h-3 me-1 inline" />
                                  {tmpl.column_count} {t('columns')}
                                </Badge>
                                {tmpl.category && (
                                  <Badge color="gray" className="text-[10px] capitalize">
                                    {tmpl.category.replace(/_/g, ' ')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Creator info */}
                          <div className="flex items-center gap-2 mb-3 px-1">
                            {isPons ? (
                              <>
                                <Logo
                                  variant="mark"
                                  whiteLabelAware={false}
                                  className="w-5 h-5 flex-shrink-0"
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {t('Created by')} <span className="font-medium text-gray-700 dark:text-gray-300">PONS</span>
                                </span>
                              </>
                            ) : (
                              /*
                                ONE CELL, normalised ONCE.

                                This read the avatar through `normalise` but
                                took `email`, `name` and `initials` straight
                                off the raw row - so a payload spelling them
                                `Name` / `Email` / `Initials`, which is exactly
                                what the templates endpoints send, drew a face
                                beside an empty name. `PersonCell` resolves
                                every field by the same rules, and renders the
                                em-dash when the payload names nobody rather
                                than asserting an "Unknown creator" who does
                                not exist.
                              */
                              <>
                                <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                                  {t('Created by')}
                                </span>
                                <PersonCell person={tmpl.owner} size="xs" />
                              </>
                            )}
                          </div>

                          {/* Column list */}
                          {tmpl.columns && tmpl.columns.length > 0 && (
                            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                              <div className="space-y-1.5">
                                {tmpl.columns.map((col: TemplateColumn, idx: number) => (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-2 text-xs"
                                  >
                                    <span className="text-gray-400 dark:text-gray-500 font-mono w-4 text-end flex-shrink-0 pt-0.5">
                                      {idx + 1}
                                    </span>
                                    <div className={`p-0.5 rounded flex-shrink-0 mt-0.5 ${isSelected ? 'text-primary-500' : 'text-gray-400'}`}>
                                      {dataTypeIcon[col.data_type] || dataTypeIcon.text}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium text-gray-700 dark:text-gray-300">
                                        {col.name}
                                      </span>
                                      {col.query && (
                                        <p className="text-gray-400 dark:text-gray-500 truncate mt-0.5 italic">
                                          {col.query}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
    </ModalShell>
  );
};

export default TemplatePickerModal;
